import { RecoilRoot } from "recoil";
import { MockedAuthManager } from "../../contexts/AuthManager";
import { MockedProvider } from "@apollo/client/testing";
import React, { useMemo, useRef } from "react";
import { createStackNavigator } from "@react-navigation/stack";
import ContractSigningScreen from "../../screens/user/ContractSigningScreen";
import { AssetStatus, ProfileType, SigningState } from "../../__generated__/graphql";
import { START_SIGNATURE_PROCESS } from "../../graphql/skribble";
import { GRAPHQL_ENDPOINT } from "../../configs/graphql.config";
import "react-native-url-polyfill/auto";
import { GET_CONTRACT } from "../../graphql/core";
import { buildAsset, buildContract, buildMissionInfo, buildUser } from "../factories";
import { Button, View } from "react-native";
import Callout from "../../ui_atoms/Callout";
import type { Meta, StoryObj } from "@storybook/react";
import { MockedResponse } from "@apollo/client/testing/core";
import { GET_ASSET } from "../../graphql/asset";
import SnackBarPortal from "../../components/SnackbarZone";
import { MockedProfileSwitcherManager } from "../../contexts/ProfileSwitcherManager";

const POSSIBLE_STATES: { [key in SigningState]?: string } = {
    [SigningState.SignFailed]: "DECLINED",
    [SigningState.SignNeeded]: "OPEN",
    [SigningState.SignProcessing]: "OPEN",
    [SigningState.SignRejected]: "WITHDRAWN",
    [SigningState.SignSuccess]: "SIGNED",
};

function buildProcess(state: SigningState) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const baseUrl = new URL(GRAPHQL_ENDPOINT || "http://www.basquiat.co/");

    const url = `${baseUrl.protocol}//${baseUrl.host}/`;
    return {
        startSignatureProcess: {
            signState: POSSIBLE_STATES[state],
            signUrl: `${url}_ui_testing/signing.html?contractId=test-contract-${state}`,
            emailSent: false,
            quality: "AES",
            signedAssetId: "00000000-0005-0000-0001-000000000000",
        },
    };
}

function buildSignatureMock(contractId: string, state: SigningState, onCall: () => void) {
    return {
        request: {
            query: START_SIGNATURE_PROCESS,
            variables: {
                contractId: contractId,
            },
        },
        newData: () => {
            onCall();
            return {
                data: buildProcess(state),
            };
        },
    };
}

type WithNavigationProps = {
    navigation: {
        reset: (x: object) => void;
    };
};

const MissionDetailsFakeScreen: React.FC<WithNavigationProps> = ({ navigation }) => {
    return (
        <View>
            <Callout markdown="Mission details screen" />
            <Button
                title="Re-do"
                onPress={() => {
                    navigation.reset({
                        index: 0,
                        routes: [{ name: "ContractSigningScreen" }],
                    });
                }}
            />
        </View>
    );
};

const HomeFakeScreen: React.FC<WithNavigationProps> = ({ navigation }) => {
    return (
        <View>
            <Callout markdown="Home screen" />
            <Button
                title="Sign contract"
                onPress={() => {
                    navigation.reset({
                        index: 0,
                        routes: [{ name: "ContractSigningScreen" }],
                    });
                }}
            />
        </View>
    );
};

const Stack = createStackNavigator<{
    Home: { screen: undefined };
    MissionDetails: { missionId: string };
    ContractSigningScreen: { contractId: string };
}>();

const StoryComponent = ({
    profileType,
    state,
}: {
    profileType: ProfileType;
    state: SigningState;
}) => {
    const assetStatuRef = useRef(AssetStatus.Pending);
    const user = useMemo(buildUser, []);
    const mock = useMemo(
        () =>
            Object.values(SigningState)
                .reduce((acc, state) => {
                    const contractId = `test-contract-${state}`;
                    acc.push(
                        buildSignatureMock(contractId, state, () => {
                            assetStatuRef.current = AssetStatus.Pending;
                            setTimeout(() => {
                                assetStatuRef.current = AssetStatus.Ready;
                            }, 5000);
                        }),
                    );
                    acc.push({
                        request: {
                            query: GET_CONTRACT,
                            variables: {
                                id: contractId,
                            },
                        },
                        newData: () => ({
                            data: {
                                getContract: {
                                    ...buildContract({
                                        id: contractId,
                                    }),
                                    ...{ missionInfo: buildMissionInfo() },
                                },
                            },
                        }),
                    });
                    return acc;
                }, [] as MockedResponse[])
                .concat([
                    {
                        variableMatcher: () => true,
                        request: {
                            query: GET_ASSET,
                        },
                        newData: () => ({
                            data: {
                                getAsset: buildAsset({
                                    status: assetStatuRef.current,
                                }),
                            },
                        }),
                    },
                ]),
        [],
    );
    return (
        <RecoilRoot>
            <MockedAuthManager user={user}>
                <MockedProfileSwitcherManager profileType={profileType}>
                    <MockedProvider mocks={mock}>
                        <Stack.Navigator initialRouteName="Home">
                            <Stack.Screen name="Home" component={HomeFakeScreen} />
                            <Stack.Screen
                                name="MissionDetails"
                                component={MissionDetailsFakeScreen}
                                initialParams={{ missionId: `test-mission-${state}` }}
                            />
                            <Stack.Screen
                                name="ContractSigningScreen"
                                component={ContractSigningScreen}
                                initialParams={{ contractId: `test-contract-${state}` }}
                                options={{
                                    gestureEnabled: false,
                                    headerShown: false,
                                    title: "Signature du contrat",
                                }}
                            />
                        </Stack.Navigator>
                    </MockedProvider>
                </MockedProfileSwitcherManager>
            </MockedAuthManager>
            <SnackBarPortal />
        </RecoilRoot>
    );
};

const meta = {
    title: "Screens/Contract Signing",
    component: StoryComponent,
    argTypes: {
        profileType: {
            options: [ProfileType.Worker, ProfileType.Client],
            control: { type: "radio" },
        },
        state: {
            options: Object.values(SigningState),
            control: { type: "radio" },
        },
    },
} satisfies Meta<typeof StoryComponent>;

export default meta;

type Story = StoryObj<typeof meta>;

export const ClientSignNeeded: Story = {
    name: "Client - SignNeeded",
    args: {
        profileType: ProfileType.Client,
        state: SigningState.SignNeeded,
    },
};

export const ClientSignSuccess: Story = {
    name: "Client - SignSuccess",
    args: {
        profileType: ProfileType.Client,
        state: SigningState.SignSuccess,
    },
};

export const ClientSignFailed: Story = {
    name: "Client - SignProcessing",
    args: {
        profileType: ProfileType.Client,
        state: SigningState.SignFailed,
    },
};
/****
 * WORKER
 * ****/

export const WorkerSignNeeded: Story = {
    name: "Worker - SignNeeded",
    args: {
        profileType: ProfileType.Worker,
        state: SigningState.SignNeeded,
    },
};

export const WorkerSignSuccess: Story = {
    name: "Worker - SignSuccess",
    args: {
        profileType: ProfileType.Worker,
        state: SigningState.SignSuccess,
    },
};

export const WorkerSignFailed: Story = {
    name: "Worker - SignProcessing",
    args: {
        profileType: ProfileType.Worker,
        state: SigningState.SignFailed,
    },
};
